import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { waLink, DEFAULT_WA_MESSAGE, BRAND } from "../../lib/site";

const links = [
  { href: "#servicos", label: "Serviços" },
  { href: "#galeria", label: "Galeria" },
  { href: "#finalizados", label: "Finalizados" },
  { href: "#cobertura", label: "Cobertura" },
  { href: "#faq", label: "FAQ" },
];

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.45 9.9-9.91A9.86 9.86 0 0 0 12.04 2zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24a8.2 8.2 0 0 1 8.24 8.25c0 4.54-3.7 8.23-8.24 8.23zm4.52-6.16c-.25-.13-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.13-1.05-.39-2-1.23-.73-.66-1.23-1.47-1.38-1.72-.14-.24-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.13-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.24-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.13.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.29z" />
  </svg>
);

export { WhatsAppIcon };

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0b0d10]/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-header-logo" className="flex items-center gap-3 group">
          <img
            src="/logo.png"
            alt="NVS Automotiva — Auto Elétrica Móvel 24h"
            className="h-11 sm:h-13 w-auto transition-transform duration-300 group-hover:scale-105 drop-shadow-[0_0_18px_rgba(232,17,45,0.35)]"
          />
          <span className="font-display font-extrabold text-lg sm:text-xl tracking-tight leading-none">
            NVS
            <span className="block text-[10px] sm:text-[11px] font-tech font-semibold uppercase tracking-[0.3em] text-[#e8112d] mt-1">
              Automotiva
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm text-slate-400 hover:text-white transition-colors duration-200 tracking-wide"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-2 font-tech text-[11px] uppercase tracking-[0.18em] text-slate-400">
            <span className="live-dot w-2 h-2 rounded-full bg-[#25D366] inline-block" />
            Plantão 24h ativo
          </div>
          <a
            href={waLink(DEFAULT_WA_MESSAGE)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-black font-semibold text-sm px-4 py-2.5 rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-[#25D366]/25 hover:scale-[1.03]"
          >
            <WhatsAppIcon className="w-4 h-4" />
            Chamar no WhatsApp
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-slate-300 p-2"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0b0d10]/95 backdrop-blur-xl border-t border-white/5 px-6 py-5 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-base text-slate-300 hover:text-[#e8112d] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href={waLink(DEFAULT_WA_MESSAGE)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-mobile-whatsapp-cta"
            className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-black font-semibold text-sm px-4 py-3 rounded-full mt-2"
          >
            <WhatsAppIcon className="w-4 h-4" />
            Chamar no WhatsApp
          </a>
        </div>
      )}
    </header>
  );
}
